/**
 * Nomination Service for Majalisna Application
 * 
 * This service handles all nomination-related API calls.
 */

import API from '../../config/api';
import { publicApiClient, privateApiClient, handleApiError } from '../apiClient';

// Normalize Saudi mobile numbers to local format 05xxxxxxxx
// Mirrors the formatting used in PersonalInfoForm.formatPhoneNumber
const normalizeSaudiMobile = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  let cleaned = String(phoneNumber).replace(/\s+/g, '');

  if (cleaned.startsWith('+966')) {
    const digits = cleaned.substring(4);
    if (digits.startsWith('5')) return `0${digits}`;
  }
  if (cleaned.startsWith('00966')) {
    const digits = cleaned.substring(5);
    if (digits.startsWith('5')) return `0${digits}`;
  }
  if (cleaned.startsWith('966')) {
    const digits = cleaned.substring(3);
    if (digits.startsWith('5')) return `0${digits}`;
  }
  if (/^5\d{8}$/.test(cleaned)) return `0${cleaned}`;
  if (/^05\d{8}$/.test(cleaned)) return cleaned;
  return phoneNumber; // return original if not recognizable
};

// Light payload normalizer to ensure any phone/mobile fields are normalized
const normalizeNominationPhones = (data) => {
  if (!data || typeof data !== 'object') return data;
  const clone = { ...data };

  const maybeNormalizeField = (obj, key) => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key) && typeof obj[key] === 'string') {
      obj[key] = normalizeSaudiMobile(obj[key]);
    }
  };

  // Top-level common fields
  maybeNormalizeField(clone, 'phone');
  maybeNormalizeField(clone, 'mobile');
  maybeNormalizeField(clone, 'contactPhone');

  // Common nested container
  if (clone.personalInfo && typeof clone.personalInfo === 'object') {
    clone.personalInfo = { ...clone.personalInfo };
    maybeNormalizeField(clone.personalInfo, 'phone');
    maybeNormalizeField(clone.personalInfo, 'mobile');
  }

  if (clone.contact && typeof clone.contact === 'object') {
    clone.contact = { ...clone.contact };
    maybeNormalizeField(clone.contact, 'phone');
    maybeNormalizeField(clone.contact, 'mobile');
  }

  return clone;
};

/**
 * Create a draft nomination
 * @param {Object} draftData - Draft nomination data
 * @returns {Promise<Object>} Created draft nomination
 */
export const createDraft = async (draftData) => {
  try {
    const payload = normalizeNominationPhones(draftData);
    const response = await privateApiClient.post(API.nominations.createDraft, payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get a nomination by ID
 * @param {string} id - Nomination ID
 * @returns {Promise<Object>} Nomination data
 */
export const getNominationById = async (id) => {
  try {
    const response = await privateApiClient.get(API.nominations.getById(id));
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update a nomination
 * @param {string} id - Nomination ID
 * @param {Object} nominationData - Updated nomination data
 * @returns {Promise<Object>} Updated nomination
 */
export const updateNomination = async (id, nominationData) => {
  try {
    const payload = normalizeNominationPhones(nominationData);
    const response = await privateApiClient.put(API.nominations.update(id), payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Submit a nomination
 * @param {Object} nominationData - Complete nomination data with fileIds
 * @returns {Promise<Object>} Submitted nomination
 */
export const submitNomination = async (nominationData) => {
  try {
    const payload = normalizeNominationPhones(nominationData);
    const response = await privateApiClient.post(API.nominations.submit, payload);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete a nomination
 * @param {string} id - Nomination ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteNomination = async (id) => {
  try {
    const response = await privateApiClient.delete(API.nominations.delete(id));
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get all nominations (admin only)
 * @returns {Promise<Array>} List of nominations
 */
export const getAllNominations = async () => {
  try {
    const response = await privateApiClient.get(API.nominations.base);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get nominations by user (for candidates)
 * @returns {Promise<Array>} List of user's nominations
 */
export const getMyNominations = async () => {
  try {
    const response = await privateApiClient.get(`${API.nominations.base}/my-nominations`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check nomination status by ID and national ID (public endpoint)
 * @param {string|number} nominationId - Nomination ID
 * @param {string} nationalId - National ID of the applicant
 * @returns {Promise<Object>} Nomination status information
 */
export const checkNominationStatus = async (nominationId, nationalId) => {
  try {
    const response = await publicApiClient.get(API.nominations.checkStatus(nominationId, nationalId));
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check if a nomination already exists for the given national ID
 * @param {string} nationalId - National ID to check
 * @returns {Promise<Object>} Response with exists flag and message
 */
export const checkExistingNomination = async (nationalId) => {
  try {
    const response = await publicApiClient.get(API.nominations.checkExisting(nationalId));
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get nominations with filters
 * @param {Object} filters - Filter parameters
 * @param {string|number} filters.branchId - Branch ID filter
 * @param {string|number} filters.centerId - Center ID filter
 * @param {string} filters.status - Status filter
 * @returns {Promise<Array>} List of filtered nominations
 */
export const getNominationsWithFilters = async (filters = {}) => {
  try {
    const { centerId, status, branchId } = filters;
    // Backend expects branch filter as branch CODE under the param name `branch_id` (string).
    // Committee users are already scoped by backend; explicit branch filter further restricts.
    const params = {};
    // Include center_id if provided (support string codes like 'mk-01')
    if (centerId != null && String(centerId).trim()) params.center_id = String(centerId).trim();
    if (status) params.status = status;
    // Branch filter: send as code string via branch_id
    if (branchId != null && String(branchId).trim()) {
      params.branch_id = String(branchId).trim();
    }

    const response = await privateApiClient.get(API.nominations.base, { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Export all functions as a service object
const nominationService = {
  createDraft,
  getNominationById,
  updateNomination,
  submitNomination,
  deleteNomination,
  getAllNominations,
  getMyNominations,
  checkNominationStatus,
  checkExistingNomination,
  getNominationsWithFilters
};

export default nominationService;
